<template>
  <v-row class="formRow" justify="center">
    <v-dialog
      v-model="showForm"
      max-width="1200px"
      :persistent="importFile !== null"
    >
      <v-form>
        <v-card>
          <v-card-title>
            <span class="headline">Import</span>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-file-input
                    v-model="importFile"
                    accept=".xlsx, .csv"
                    label="Select the Spreadsheet File"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6">
                  <v-checkbox label="Spreadsheet has header row" v-model="importFileHasHeaders"/>
                </v-col>
                <v-col class="text-right" cols="6">
                  <v-btn color="primary" outlined @click="parseImportFile" :disabled="isParsing" :loading="isParsing">Parse Spreadsheet</v-btn>
                </v-col>
              </v-row>

              <div v-if="parsedHeaders.length > 0">
                <zig-zag />
                <v-row>
                  <v-col>
                    <inventory-class-select-element
                      v-model="classId"
                      :is-required="true"
                      :is-add-all="false"
                    />
                  </v-col>
                  <v-col v-if="businessRulesGetBoolean('Inventory Sub Class')" cols="6">
                    <v-autocomplete
                      v-model="subClasses"
                      label="Sub Classes"
                      :items="subClassesList"
                      item-text="name"
                      item-value="id"
                      multiple
                    >
                      <template #item="{ item }">
                        <v-chip
                          class="mt-1 mb-1"
                          small
                          text-color="white"
                          style="cursor: pointer;"
                          :color="item ? item.color : 'white'"
                        >
                          {{ item ? item.name : '' }}
                        </v-chip>
                      </template>
                      <template #selection="{ item }">
                        <v-chip
                          class="mt-1 mb-1"
                          small
                          text-color="white"
                          style="cursor: pointer;"
                          :color="item ? item.color : 'white'"
                        >
                          {{ item ? item.name : '' }}
                        </v-chip>
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="text-center headline" cols="4">
                    Value
                  </v-col>
                  <v-col class="text-center headline" cols="4">
                    Spreadsheet Column
                  </v-col>
                  <v-col class="text-center headline" cols="4">
                    Default Value
                  </v-col>
                </v-row>

                <v-row v-for="header in headers" :key="`header-${header.name}`" class="mt-0">
                  <v-col cols="4" align-self="center">
                    {{ header.name }}
                    <RedAsterisk v-if="header.isRequired"/>
                  </v-col>
                  <v-col cols="4">
                    <v-select :items="parsedHeaders" item-value="index" v-model="header.index" density="compact" hide-details="auto" :disabled="parsedHeaders.length === 0">
                      <template #item="{ item }">
                        <span v-if="item.name">{{ item.name }}</span>
                        <span v-else>[No Header]: Column {{ item.index + 1  }}</span>
                      </template>
                      <template #selection="{ item }">
                        <span v-if="item.name">{{ item.name }}</span>
                        <span v-else>[No Header]: Column {{ item.index + 1  }}</span>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="4" v-if="header.defaultType !== false">
                    <v-text-field v-if="header.defaultType === 'input'" v-model="header.defaultValue"/>
                    <v-select v-if="header.defaultType === 'dropdown'" v-model="header.defaultValue" :items="header.defaultValues" item-value="id" item-text="name"/>
                  </v-col>
                </v-row>
              </div>

              <div v-if="importResponse">
                <zig-zag />
                <v-row>
                  <v-col class="headline" cols="4">Import Status</v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <div class="text-h6">
                      <v-btn icon @click="newItemsShown = !newItemsShown">
                        <v-icon>{{ newItemsShown ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                      </v-btn>
                      {{ importResponse.newItems.length }} Inventory Item(s) successfully imported
                      <v-icon v-if="importResponse.newItems.length > 0" @click="downloadCsv(importResponse.newItems, 'newItems')">mdi-file-delimited-outline</v-icon>
                    </div>
                    <div class="ml-10" v-if="newItemsShown">
                      <div v-for="newItem in importResponse.newItems">
                        Row: {{ newItem.row }}, Material Id: {{ newItem.materialId }}
                      </div>
                    </div>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col>
                    <div class="text-h6">
                      <v-btn icon @click="repeatItemsShown = !repeatItemsShown">
                        <v-icon>{{ repeatItemsShown ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                      </v-btn>
                      {{ importResponse.repeatItems.length }} repeat Inventory Item(s)
                      <v-icon v-if="importResponse.repeatItems.length > 0" @click="downloadCsv(importResponse.repeatItems, 'repeatItems')">mdi-file-delimited-outline</v-icon>
                    </div>
                    <div class="ml-10" v-if="repeatItemsShown">
                      <div v-for="repeatItem in importResponse.repeatItems">
                        Row: {{ repeatItem.row }}, Material Id: {{ repeatItem.materialId }}
                      </div>
                    </div>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col>
                    <div class="text-h6">
                      <v-btn icon @click="errorsShown = !errorsShown">
                        <v-icon>{{ errorsShown ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                      </v-btn>
                      {{ importResponse.errors.length }} errors
                      <v-icon v-if="importResponse.errors.length > 0" @click="downloadCsv(importResponse.errors, 'errors')">mdi-file-delimited-outline</v-icon>
                    </div>
                    <div class="ml-10" v-if="errorsShown">
                      <div v-for="error in importResponse.errors">
                        <span v-if="error.row !== null">
                          Row: {{ error.row }}, Material Id: {{ error.materialId }}, Error: {{ error.error }}
                        </span>
                        <span v-else>
                          Generic error: {{ error.error }}
                        </span>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="normal" class="mr-2" @click="closeForm">
              Cancel
            </v-btn>
            <v-btn color="primary" v-if="parsedHeaders.length > 0" @click="submitImport" :disabled="isImporting" :loading="isImporting">
              Import
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

  </v-row>
</template>

<script>
// import colorsApi from '../../api/ColorsApi';
import { mapGetters } from 'vuex';
import inventoryCategoriesApi from '../../api/InventoryCategoriesApi';
import inventoryItemsApi from '../../api/InventoryItemsApi';
import lengthsApi from '../../api/LengthsApi';
import uomsApi from '../../api/UomsApi';
import InventoryClassSelectElement from '../elements/InventoryClassSelectElement.vue';
import ZigZag from '@/components/elements/ZigZag';
import moment from 'moment';

export default {
  components: { ZigZag, InventoryClassSelectElement },
  data: () => ({
    showForm: false,
    importFile: null,
    isParsing: false,
    isImporting: false,
    importFileHasHeaders: true,
    fileContent: null,
    parsedHeaders: [],
    classId: null,
    importResponse: null,
    newItemsShown: false,
    repeatItemsShown: false,
    errorsShown: false,
    subClasses: [],
    subClassesList: [],

    headers: [
      {
        name: 'Material Id',
        field: 'materialId',
        isRequired: true,
        index: null,
        defaultType: false,
      },
      {
        name: 'Name',
        field: 'name',
        isRequired: true,
        index: null,
        defaultType: false,
      },
      {
        name: 'UOMs',
        field: 'uom',
        isRequired: true,
        index: null,
        defaultType: 'dropdown',
        defaultValue: null,
        defaultValues: [],
      },
      {
        name: 'Weight',
        field: 'weight',
        isRequired: false,
        index: null,
        defaultType: 'input',
        defaultValue: null,
      },
      {
        name: 'Category',
        field: 'category',
        isRequired: false,
        index: null,
        defaultType: 'dropdown',
        defaultValue: null,
        defaultValues: [],
      },
      {
        name: 'Length',
        field: 'length',
        isRequired: false,
        index: null,
        defaultType: 'dropdown',
        defaultValue: null,
        defaultValues: [],
      },
      // {
      //   name: 'Colour Code',
      //   field: 'colourCodes',
      //   isRequired: false,
      //   index: null,
      //   defaultType: 'dropdown',
      //   defaultValue: null,
      //   defaultValues: [],
      // },
      {
        name: 'Price',
        field: 'price',
        isRequired: false,
        index: null,
        defaultType: 'input',
        defaultValue: null,
      },
    ]
  }),
  mounted: function() {
    this.loadDefaultValues();
  },
  computed: {
    ...mapGetters([
      'businessRulesGetBoolean',
    ]),
  },
  methods: {
    openDialog() {
      this.clearForm();
      this.showForm = true;
    },
    async loadDefaultValues() {
      const defaultValuesMap = {
        uom: () => uomsApi.getList(),
        category: () => inventoryCategoriesApi.getList(),
        length: () => lengthsApi.getList(),
        // colourCodes: () => colorsApi.getList(),
      }
      for (let i = 0; i < this.headers.length; i++) {
        const header = this.headers[i];
        if (!(header.field in defaultValuesMap) || header.defaultType !== 'dropdown') continue;
        header.defaultValues = await defaultValuesMap[header.field]();
      }

      this.subClassesList = await this.$api.inventorySubClass.getList();
    },
    async parseImportFile() {
      if (!this.importFile) return this.showSnackbarError('Please select a spreadsheet file');
      this.isParsing = true;
      try {
        const formData = new FormData();
        formData.append('hasHeaders', this.importFileHasHeaders);
        formData.append('file', this.importFile);
        this.parsedHeaders = await inventoryItemsApi.parseSpreadsheetHeaders(formData);
      } catch (e) {
        console.error(e);
      } finally {
        this.isParsing = false;
      }
    },
    async submitImport() {
      if (this.classId === null || this.headers.filter(x => (x.index === null && x.defaultValue === null) && x.isRequired).length > 0) return this.showSnackbarError('Please select all the required fields');
      const selectedHeaders = this.headers.filter(x => x.index !== null || x.defaultValue !== null);
      this.isImporting = true;
      try {
        const formData = new FormData();
        formData.append('file', this.importFile);
        formData.append('hasHeaders', this.importFileHasHeaders);
        formData.append('classId', this.classId);
        formData.append('headerCount', selectedHeaders.length);
        selectedHeaders.forEach((x, i) => {
          formData.append(`headers[${i}][index]`, x.index);
          formData.append(`headers[${i}][field]`, x.field);
          if (x.defaultValue !== null) formData.append(`headers[${i}][defaultValue]`, x.defaultValue);
        });
        if (this.businessRulesGetBoolean('Inventory Sub Class') && this.subClasses.length > 0) {
          this.subClasses.forEach((x, i) => {
            formData.append(`subclasses[${i}]`, x);
          });
        }
        this.importResponse = await inventoryItemsApi.importSpreadsheet(formData);
      } catch (e) {
        console.error(e);
      } finally {
        this.isImporting = false;
      }

    },
    showSnackbarError(message) {
      this.$bus.$emit('showError', message);
    },
    async clearForm() {
      this.importFile = null;
      this.fileContent = null;
      this.parsedHeaders = [];
      this.importResponse = null;
      this.subClasses = [];
      this.headers.forEach(header => header.index = null);
    },
    closeForm() {
      this.clearForm();
      this.showForm = false;
    },
    downloadCsv(dataArray, name) {
      if (dataArray.length === 0) return;
      const keys = Object.keys(dataArray[0]);
      const headers = keys.map(key => key.substring(0, 1).toUpperCase() + key.substring(1)).join(',');
      const valuesArray = [];
      for (let i = 0; i < dataArray.length; i++) {
        valuesArray.push(Object.values(dataArray[i]).map(value => {
          const cell = value === null ? '' : String(value);
          return `"${cell.replace('""', '""')}"`
        }).join(','));
      }
      const csvString = [headers, valuesArray.join('\n')].join('\n');
      const csvBlob = new Blob([csvString], { type: 'text/csv' });
      const url = URL.createObjectURL(csvBlob);
      const aEl = document.createElement('a');
      aEl.href = url;
      aEl.download = `import_${name}_${moment().format('YYYY-Mm-DD_HH:mm:ss')}.csv`;
      aEl.click();
    }
  },
};
</script>

<style scoped>
.mrp-btn-add {
  min-width: 35px !important;
  padding: 0 !important;
}
</style>
